<script setup>
  import { computed, onMounted } from 'vue';
  import { toCurrency } from '@/utils/math';
  import BaseButton from '@/shared/components/base/BaseButton.vue';
  import BaseCard from '@/shared/components/base/BaseCard.vue';
  import { cartV2Store } from '@/store/cartV2';

  const emit = defineEmits(['checkout', 'event-updated']);

  // Use cart store
  const cartStore = cartV2Store();

  // Group items by event
  const itemsByEvent = computed(() => {
    const grouped = {};

    for (const item of cartStore.items || []) {
      // V2 API uses 'type' instead of 'item_type'
      if (item.type !== 'ticket') continue;

      const eventId = item.metadata?.event_id;
      if (!eventId) continue;

      if (!grouped[eventId]) {
        grouped[eventId] = {
          eventId: parseInt(eventId),
          eventTitle:
            item.metadata?.event_title || item.metadata?.event_name || '',
          eventDate: item.metadata?.event_date || '',
          eventTime: item.metadata?.event_time || '',
          items: [],
          total: 0,
        };
      }

      grouped[eventId].items.push(item);
      grouped[eventId].total += parseFloat(item.unit_price) * item.quantity;
    }

    return Object.values(grouped);
  });

  // Calculate totals
  const subtotal = computed(() => parseFloat(cartStore.totals?.subtotal || 0));
  const total = computed(() => parseFloat(cartStore.totals?.total || 0));
  const ticketCount = computed(() => cartStore.totals?.count || 0);

  // Discounts (from metadata or separate field)
  const discounts = computed(() => cartStore.metadata?.discounts || []);

  // Check if cart has items
  const hasItems = computed(() => (cartStore.items?.length || 0) > 0);

  // Check if all items are products (not tickets)
  const isProductCart = computed(() => {
    const items = cartStore.items || [];
    if (items.length === 0) return false;
    return items.every(
      (item) => item.metadata?.event_ticket_type === 'products'
    );
  });

  // Dynamic label for checkout button
  const itemLabel = computed(() => {
    if (isProductCart.value) {
      return ticketCount.value === 1 ? 'product' : 'producten';
    }
    return ticketCount.value === 1 ? 'ticket' : 'tickets';
  });

  // Format item display name
  function getItemDisplayName(item) {
    if (item.metadata?.seat_name || item.metadata?.seat) {
      return `Plaats ${item.metadata?.seat_name || item.metadata?.seat}`;
    }
    return item.metadata?.ticket_name || item.name || 'Ticket';
  }

  // Check if item has discount code
  function hasDiscountCode(item) {
    return item.metadata?.code && item.metadata?.code !== '';
  }

  // Get original price for discounted items
  function getOriginalPrice(item) {
    // If there's a discount code, show the original price
    if (hasDiscountCode(item) && item.metadata?.original_price) {
      return parseFloat(item.metadata.original_price);
    }
    return null;
  }

  // Get variant info from item
  function getVariantInfo(item) {
    if (!item.metadata?.selected_variant) {
      return null;
    }

    const variant = item.metadata.selected_variant;
    const label = variant.label || '';
    const priceModifier = parseFloat(variant.price_modifier || 0);

    return {
      label,
      priceModifier,
      hasModifier: priceModifier > 0,
    };
  }

  // Calculate item total price including modifier
  function getItemTotalPrice(item) {
    const basePrice = parseFloat(item.unit_price) * item.quantity;
    const modifier = item.price_modifier || 0;
    return basePrice + modifier * item.quantity;
  }

  function usesBulkEventQuantityUpdate(item) {
    return (
      item.type === 'ticket' &&
      item.metadata?.event_ticket_type !== 'products' &&
      !item.metadata?.seat_id &&
      !item.metadata?.seat_name &&
      !item.metadata?.seat &&
      !item.metadata?.selected_variant
    );
  }

  function buildBulkEventQuantityItems(eventId, removedItem) {
    const quantitiesByType = {};

    for (const cartItem of cartStore.items || []) {
      if (!usesBulkEventQuantityUpdate(cartItem)) continue;
      if (parseInt(cartItem.metadata?.event_id) !== parseInt(eventId)) continue;

      quantitiesByType[cartItem.item_id] =
        (quantitiesByType[cartItem.item_id] || 0) + cartItem.quantity;
    }

    quantitiesByType[removedItem.item_id] = Math.max(
      0,
      (quantitiesByType[removedItem.item_id] || 0) - removedItem.quantity
    );

    return Object.entries(quantitiesByType).map(([ticketTypeId, quantity]) => ({
      ticketTypeId: parseInt(ticketTypeId),
      quantity,
    }));
  }

  async function removeItem(item) {
    try {
      cartStore.setLoading(true);

      const response = usesBulkEventQuantityUpdate(item)
        ? await fetch('/api/v2/cart/set-event-ticket-quantities', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              eventId: parseInt(item.metadata?.event_id),
              items: buildBulkEventQuantityItems(item.metadata?.event_id, item),
            }),
          })
        : await fetch('/api/v2/cart/remove-ticket', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              eventId: item.metadata?.event_id,
              ticketTypeId: item.item_id,
              quantity: item.quantity,
              metadata: item.metadata || {},
            }),
          });

      if (!response.ok) {
        throw new Error(`Failed to remove ticket: ${response.status}`);
      }

      const data = await response.json();
      cartStore.setCart(data.cart_summary || data);

      if (data.event_summary) {
        emit('event-updated', data.event_summary);
      }
    } catch (error) {
      console.error('Failed to remove ticket from cart:', error);
      alert('Er ging iets mis bij het verwijderen van het ticket');
    } finally {
      cartStore.setLoading(false);
    }
  }

  function checkout() {
    emit('checkout');
  }

  // Lifecycle hooks
  onMounted(async () => {
    await cartStore.loadCart();
  });
</script>

<template>
  <BaseCard>
    <template #header>
      <h2 class="text-lg font-semibold text-gray-900">Winkelmand</h2>
    </template>

    <!-- Empty state -->
    <div v-if="!hasItems" class="py-8 text-center text-gray-500">
      Nog niets in je winkelmandje
    </div>

    <!-- Cart items -->
    <div v-else class="space-y-6">
      <!-- Items grouped by event -->
      <div
        v-for="eventGroup in itemsByEvent"
        :key="`event-${eventGroup.eventId}`"
        class="pb-4"
      >
        <!-- Event header -->
        <div class="mb-3">
          <div class="font-semibold text-gray-900">
            {{ eventGroup.eventTitle }}
            <span
              v-if="eventGroup.eventDate"
              class="ml-2 text-sm font-normal text-gray-500"
            >
              {{ eventGroup.eventDate }}
            </span>
          </div>
        </div>

        <!-- Event items -->
        <div class="space-y-2">
          <div
            v-for="(item, index) in eventGroup.items"
            :key="`item-${item.id || index}`"
            class="flex items-center justify-between rounded-lg border border-gray-200 p-3"
          >
            <div class="flex-1">
              <div class="font-medium text-gray-900">
                {{ getItemDisplayName(item) }}
              </div>
              <div v-if="getVariantInfo(item)" class="text-xs text-gray-600">
                <i class="fa-solid fa-shirt"></i>
                {{ getVariantInfo(item).label }}
                <span
                  v-if="getVariantInfo(item).hasModifier"
                  class="text-gray-500"
                >
                  (+{{ toCurrency(getVariantInfo(item).priceModifier) }})
                </span>
              </div>
              <div v-if="hasDiscountCode(item)" class="text-xs text-green-600">
                <i class="fa-solid fa-tag"></i>
                Code: {{ item.metadata.code }}
              </div>
            </div>

            <div class="flex items-center gap-4">
              <!-- Quantity -->
              <div class="text-sm text-gray-600">{{ item.quantity }}x</div>

              <!-- Price -->
              <div class="min-w-[80px] text-right">
                <div
                  v-if="getOriginalPrice(item)"
                  class="text-xs text-gray-400 line-through"
                >
                  {{ toCurrency(getOriginalPrice(item)) }}
                </div>
                <div class="font-semibold text-gray-900">
                  {{ toCurrency(getItemTotalPrice(item)) }}
                </div>
              </div>

              <!-- Remove button -->
              <button
                class="text-red-600 hover:text-red-800"
                title="Verwijderen"
                @click="removeItem(item)"
              >
                <i class="fa-solid fa-trash-can"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Totals section -->
      <div class="space-y-3 border-t border-gray-200 pt-4">
        <!-- Subtotal -->
        <div class="flex items-center justify-between text-sm">
          <span class="text-gray-600">Subtotaal</span>
          <span class="font-semibold text-gray-900">
            {{ toCurrency(subtotal) }}
          </span>
        </div>

        <!-- Discounts (if any) -->
        <div v-if="discounts && discounts.length > 0" class="space-y-2">
          <div class="flex items-center justify-between text-sm">
            <span class="text-gray-600">Kortingen</span>
            <span class="font-semibold text-green-600">
              -{{
                toCurrency(
                  discounts.reduce(
                    (sum, d) => sum + parseFloat(d.amount || 0),
                    0
                  )
                )
              }}
            </span>
          </div>
          <div class="space-y-1 pl-4">
            <div
              v-for="(discount, dIndex) in discounts"
              :key="`discount-${dIndex}`"
              class="flex items-center justify-between text-xs text-gray-500"
            >
              <span class="flex items-center gap-1.5">
                <i class="fa-solid fa-tag text-green-600"></i>
                {{ discount.label }}
              </span>
              <span>
                {{ discount.amount }} {{ discount.isPercent ? '%' : '€' }}
              </span>
            </div>
          </div>
        </div>

        <!-- Total -->
        <div
          class="flex items-center justify-between border-t-2 border-gray-200 pt-3"
        >
          <span class="text-base font-semibold text-gray-900">Totaal</span>
          <span class="text-primary-600 text-xl font-bold">
            {{ toCurrency(total) }}
          </span>
        </div>
      </div>

      <!-- Checkout button -->
      <div class="border-t border-gray-200 pt-4">
        <BaseButton
          color="primary"
          class="w-full"
          :disabled="cartStore.loading || !hasItems"
          @click="checkout"
        >
          Afrekenen ({{ ticketCount }} {{ itemLabel }})
        </BaseButton>
      </div>
    </div>
  </BaseCard>
</template>
